import { Link } from 'react-router-dom';
import type { Issue } from '../api/client';
import {
  STATUS_LABEL,
  STATUS_BADGE_CLASS,
  PRIORITY_LABEL,
  PRIORITY_BADGE_CLASS,
} from '../lib/format';

export interface IssuesTableProps {
  issues: Issue[];
  /** Build the href for an issue row's Title link. */
  buildIssueHref: (i: Issue) => string;
  /** Show the Reporter column (admin only — customer issues are always themselves). */
  showReporter?: boolean;
}

/**
 * Per-row "awaiting staff" indicator — true when the issue currently
 * needs staff eyes. Drives the small red dot rendered before the title
 * (iPhone Mail-app "unread" pattern). Same filter as the AdminIssuesPage
 * client-side counter; both derive from the loaded issue's awaitingAction
 * field, so they stay in sync.
 */
function awaitsStaff(i: Issue): boolean {
  return i.awaitingAction === 'staff' && i.status !== 'resolved';
}

/** Small filled red dot indicating "awaiting staff."
 *  - aria-hidden so the dot doesn't pollute the parent link's accessible
 *    name (the link's accessible name stays = the issue title).
 *  - title attribute gives sighted users a tooltip on hover.
 *  - Keep aria-label off; add a separate sr-only span for screen readers
 *    only when accessibility coverage becomes a priority. */
function AwaitingDot(): JSX.Element {
  return (
    <span
      data-testid="awaiting-staff-dot"
      aria-hidden="true"
      title="Awaiting staff"
      className="inline-block w-2 h-2 rounded-full bg-red-500 mr-2 align-middle shrink-0"
    />
  );
}

export function IssuesTable({
  issues,
  buildIssueHref,
  showReporter = false,
}: IssuesTableProps): JSX.Element {
  const colCount = 7 + (showReporter ? 1 : 0);
  return (
    <div className="card p-0">
      {/* Mobile card list */}
      <ul className="sm:hidden divide-y divide-gray-800">
        {issues.map((i) => (
          <li key={i.issueId} className="p-4 hover:bg-gray-800/40 transition-colors">
            <div className="flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <Link
                  to={buildIssueHref(i)}
                  className="text-red-400 hover:text-red-300 hover:underline font-medium text-sm"
                >
                  {awaitsStaff(i) && <AwaitingDot />}
                  {i.title}
                </Link>
                {i.issueReferenceNumber && (
                  <div className="text-xs font-mono text-gray-500 mt-0.5">
                    {i.issueReferenceNumber}
                  </div>
                )}
              </div>
              <div className="flex flex-col items-end gap-1 shrink-0">
                <span className={`badge ${STATUS_BADGE_CLASS[i.status]}`}>
                  {STATUS_LABEL[i.status]}
                </span>
                <span className={`badge ${PRIORITY_BADGE_CLASS[i.priority]}`}>
                  {PRIORITY_LABEL[i.priority]}
                </span>
              </div>
            </div>
            <div className="mt-2 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-gray-500">
              {i.ownerAddress && <span className="text-gray-400">{i.ownerAddress}</span>}
              {showReporter && (i.ownerEmail ?? i.ownerSub) && (
                <span>{i.ownerEmail ?? i.ownerSub}</span>
              )}
              <span>{new Date(i.createdAt).toLocaleDateString()}</span>
            </div>
          </li>
        ))}
        {issues.length === 0 && (
          <li className="p-4 text-center text-gray-500 text-sm">No issues</li>
        )}
      </ul>

      {/* Desktop table */}
      <div className="hidden sm:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-800/60 text-left text-gray-400 border-b border-gray-700">
            <tr>
              <th className="p-3">Reference number</th>
              <th className="p-3">Title</th>
              <th className="p-3">Status</th>
              <th className="p-3">Priority</th>
              {showReporter && <th className="p-3">Reporter</th>}
              <th className="p-3">Address</th>
              <th className="p-3">Created</th>
              <th className="p-3">Updated</th>
            </tr>
          </thead>
          <tbody>
            {issues.map((i) => (
              <tr
                key={i.issueId}
                className="border-t border-gray-800 hover:bg-gray-800/40 transition-colors"
              >
                <td className="p-3 text-xs font-mono text-gray-500 whitespace-nowrap">
                  {i.issueReferenceNumber ?? '—'}
                </td>
                <td className="p-3">
                  <Link
                    to={buildIssueHref(i)}
                    className="text-red-400 hover:text-red-300 hover:underline"
                  >
                    {awaitsStaff(i) && <AwaitingDot />}
                    {i.title}
                  </Link>
                </td>
                <td className="p-3">
                  <span className={`badge ${STATUS_BADGE_CLASS[i.status]}`}>
                    {STATUS_LABEL[i.status]}
                  </span>
                </td>
                <td className="p-3">
                  <span className={`badge ${PRIORITY_BADGE_CLASS[i.priority]}`}>
                    {PRIORITY_LABEL[i.priority]}
                  </span>
                </td>
                {showReporter && (
                  <td className="p-3 text-xs text-gray-300">
                    {i.ownerEmail ?? i.ownerSub}
                  </td>
                )}
                <td className="p-3 text-xs text-gray-400">
                  {i.ownerAddress ?? <span className="text-gray-600">—</span>}
                </td>
                <td className="p-3 text-xs text-gray-500">
                  {new Date(i.createdAt).toLocaleString()}
                </td>
                <td className="p-3 text-xs text-gray-500">
                  {new Date(i.updatedAt).toLocaleString()}
                </td>
              </tr>
            ))}
            {issues.length === 0 && (
              <tr>
                <td colSpan={colCount} className="p-4 text-center text-gray-500">
                  No issues
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
